import { useSyncExternalStore } from "react";

/** Visitor preferences live only on the visitor's device (localStorage). LUX stores nothing about public visitors. */
const KEY_BLUR = "lux_discreet";
const KEY_SAVED = "lux_saved";
const MAX_SAVED = 60;

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}

let state = { blur: !!read(KEY_BLUR, false), saved: Array.isArray(read(KEY_SAVED, [])) ? read(KEY_SAVED, []) : [] };
const listeners = new Set();

function commit(patch) {
  state = { ...state, ...patch };
  try {
    localStorage.setItem(KEY_BLUR, JSON.stringify(state.blur));
    localStorage.setItem(KEY_SAVED, JSON.stringify(state.saved));
  } catch (e) {
    /* private mode: keep in memory only */
  }
  listeners.forEach((l) => l());
}

function subscribe(l) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function setBlur(v) {
  commit({ blur: !!v });
}

export function toggleSaved(slug) {
  const has = state.saved.includes(slug);
  const saved = has ? state.saved.filter((s) => s !== slug) : [slug, ...state.saved].slice(0, MAX_SAVED);
  commit({ saved });
  return !has;
}

export function removeSaved(slugs) {
  commit({ saved: state.saved.filter((s) => !slugs.includes(s)) });
}

export function clearSaved() {
  commit({ saved: [] });
}

export function useVisitor() {
  const s = useSyncExternalStore(subscribe, () => state, () => state);
  return { blur: s.blur, saved: s.saved, isSaved: (slug) => s.saved.includes(slug), setBlur, toggleSaved, removeSaved, clearSaved };
}

/** Keys LUX keeps on the visitor's device (all optional preferences + the random device id). */
export const DEVICE_STORAGE = [
  { key: "lux_age_confirmed", store: "local", label: "18+ confirmation" },
  { key: "lux_discreet", store: "local", label: "Blur photos setting" },
  { key: "lux_saved", store: "local", label: "Saved shortlist" },
  { key: "lux_device", store: "local", label: "Random device identifier" },
  { key: "lux_splash_seen", store: "session", label: "Entry animation shown this session" },
];

/** What is currently stored, for display. */
export function deviceStorageStatus() {
  return DEVICE_STORAGE.map((d) => {
    let present = false;
    try {
      const st = d.store === "session" ? sessionStorage : localStorage;
      present = st.getItem(d.key) !== null;
    } catch (e) {
      present = false;
    }
    return { ...d, present };
  });
}

/** Erase everything LUX stored on this device. Returns the number of items removed. */
export function resetDevice() {
  let removed = 0;
  for (const d of DEVICE_STORAGE) {
    try {
      const st = d.store === "session" ? sessionStorage : localStorage;
      if (st.getItem(d.key) !== null) {
        st.removeItem(d.key);
        removed += 1;
      }
    } catch (e) {
      /* ignore */
    }
  }
  state = { blur: false, saved: [] };
  listeners.forEach((l) => l());
  return removed;
}

/** Leaves LUX immediately for a neutral site (Quick exit). */
export const QUICK_EXIT_URL = "https://www.bbc.co.uk/weather";
export function quickExit() {
  try {
    window.open(QUICK_EXIT_URL, "_blank", "noopener");
  } catch (e) {
    /* ignore */
  }
  window.location.replace(QUICK_EXIT_URL);
}
